import { transformEnumToDisplay } from '@fruitful/fruitils';
import {
    HasKids,
    type MembershipProfileAnswerUpsertDto,
    type MembershipProfileKidAnswer,
    QuestionKey,
} from '@fruitful/member-profile';
import { zodResolver } from '@hookform/resolvers/zod';
import { router } from 'expo-router';
import { useCallback, useMemo } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import {
    BodyLargeRegular,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Form,
    FullScreenSpinner,
    ScreenContainer,
    SelectSingle,
    USE_FORM_MODE_DEFAULTS,
    useToastController,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { useViewEvents } from '../../events/hooks/useViewEvents';
import { captureException } from '../../logger/Logger';
import useGetMembershipProfileAnswers from '../hooks/useGetMembershipProfileAnswers';
import { useTrackFinancialProfileCompletion } from '../hooks/useTrackFinancialProfileCompletion';
import useUpsertMembershipProfileAnswer from '../hooks/useUpsertMembershipProfileAnswer';
import {
    transformDataToMembershipProfileAnswerUpsertDto,
    transformMembershipProfileAnswerToUpsertDto,
    transformUserToMemberProfileMembership,
} from '../utils/memberProfileUtils';
import {
    SUCCESS_TOAST_DURATION,
    SUCCESS_TOAST_MESSAGE,
    SuccessToastIcon,
} from './SuccessToast';

export const KidsHasKids = () => {
    const { trackViewOnce } = useViewEvents();
    trackViewOnce('Profile/Kids');

    const toast = useToastController();
    const user = useAuthedUser();
    const membership = transformUserToMemberProfileMembership(user);

    const { trackFinancialProfileUpdate } =
        useTrackFinancialProfileCompletion();

    const {
        data: kids,
        isLoading: isLoadingProfileAnswers,
        refetch: refetchKidsAnswer,
    } = useGetMembershipProfileAnswers<MembershipProfileKidAnswer>({
        questionKey: QuestionKey.KIDS,
    });

    const { mutateAsync: upsertMembershipProfileAnswer } =
        useUpsertMembershipProfileAnswer();

    const existingData = useMemo(() => {
        return kids != null
            ? transformMembershipProfileAnswerToUpsertDto<MembershipProfileKidAnswer>(
                  kids,
              )
            : undefined;
    }, [kids]);

    const hasKidsEnumValues = useMemo(() => Object.values(HasKids), []);
    const hasKidsOptions = useMemo(
        () =>
            hasKidsEnumValues.map((status) => ({
                title: transformEnumToDisplay(status),
            })),
        [hasKidsEnumValues],
    );

    const kidsHasKidsSchema = z.object({
        hasKids: z.nativeEnum(HasKids),
    });

    type KidsHasKidsSchema = z.infer<typeof kidsHasKidsSchema>;

    const {
        handleSubmit,
        formState: { isValid },
        setValue,
        watch,
    } = useForm<KidsHasKidsSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(kidsHasKidsSchema),
        defaultValues: {
            hasKids: existingData?.answer.answer,
        },
    });

    const watchedHasKids = watch('hasKids');

    const selectedIndex = useMemo(() => {
        return watchedHasKids != null
            ? hasKidsEnumValues.findIndex((v) => v === watchedHasKids)
            : -1;
    }, [hasKidsEnumValues, watchedHasKids]);

    const onSubmit: SubmitHandler<KidsHasKidsSchema> = useCallback(
        async ({ hasKids }) => {
            try {
                // If HasKids is Yes, then proceed to kids details page.
                if (hasKids === HasKids.YES) {
                    router.navigate('/profile/kids/info');
                    return;
                }

                // Otherwise, persist the value and clear out any existing kids details data.
                const dto: MembershipProfileAnswerUpsertDto<MembershipProfileKidAnswer> =
                    transformDataToMembershipProfileAnswerUpsertDto<MembershipProfileKidAnswer>(
                        {
                            answer: hasKids,
                            ...(hasKids === HasKids.NO
                                ? { additionalInformation: [] }
                                : {}),
                        },
                        QuestionKey.KIDS,
                        membership,
                        user,
                    );

                await upsertMembershipProfileAnswer({ body: [dto] });

                toast.show(SUCCESS_TOAST_MESSAGE, {
                    duration: SUCCESS_TOAST_DURATION,
                    icon: SuccessToastIcon,
                });

                trackFinancialProfileUpdate({ kidsUpdated: kids != null });

                void refetchKidsAnswer();
                router.navigate('/profile/financial-profile');
            } catch (exception) {
                captureException(exception);
                toast.show('Something went wrong!', { duration: 2000 });
            }
        },
        [
            membership,
            refetchKidsAnswer,
            toast,
            upsertMembershipProfileAnswer,
            user,
        ],
    );

    if (isLoadingProfileAnswers) {
        return <FullScreenSpinner />;
    }

    return (
        <ScreenContainer title="Kids" hasBackButton={true}>
            <YStack gap={24} justifyContent="space-between">
                <YStack gap={16}>
                    <BodyLargeRegular>Do you have any kids?</BodyLargeRegular>
                    <BodyMediumRegular color="$gray1000">
                        This helps us give you financial advice that fits your
                        family situation - like saving for college or planning
                        for family expenses.
                    </BodyMediumRegular>

                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <SelectSingle
                            options={hasKidsOptions}
                            selectedIndex={selectedIndex}
                            onSelect={(index) => {
                                setValue(
                                    'hasKids',
                                    hasKidsEnumValues[index] as HasKids,
                                    {
                                        shouldValidate: true,
                                        shouldDirty: true,
                                    },
                                );
                            }}
                        />
                    </Form>
                </YStack>

                <Button
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    text="Continue"
                    onPress={handleSubmit(onSubmit)}
                    disabled={!isValid}
                />
            </YStack>
        </ScreenContainer>
    );
};

export default KidsHasKids;
